import { computed, inject } from '@angular/core';
import { RestApiClientService } from '@core/rest-api-client/rest-api-client.service';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  aboutAppAttributes,
  homePageAttributes,
  howJobWorkAttributes,
  newsPageAttributes,
  peopleAndGroupAttributes,
  peopleCardAttributes
} from '@state/utils';
import { EntityType } from '@utils/enum/entity-type.enum';
import { IMenuItemLink } from '@utils/interfaces/menu-item-link.model';
import { catchError, map, switchMap } from 'rxjs/operators';

export interface IMainMenuDropdown {
  items: IMenuItemLink[];
  link: IMenuItemLink;
}

export enum FooterMenuColumnType {
  Default = 'default',
  Bordered = 'bordered'
}

export interface IFooterMenuSection {
  label?: string;
  icon?: string;
  menu_items: IMenuItemLink[];
}

export interface IFooterMenuColumn {
  label?: string;
  icon?: string;
  type: FooterMenuColumnType;
  sections: IFooterMenuSection[];
}
// Footer Menu State
export interface FooterMenuState {
  columns: IFooterMenuColumn[];
  isLoading: boolean;
  error: string | null;
}

// Main Menu State
export interface MainMenuState {
  menu_items: any[];
  isLoading: boolean;
  error: string | null;
}

// People Cards State
export interface PeopleCardsState {
  items: any[];
  paginatedItems: any[];
  footerItems: any[];
  totalItem: number;
  isLoading: boolean;
  error: string | null;
}

// Pages State
export interface PagesState {
  items: Map<string, any>;
  isLoading: boolean;
  error: string | null;
}

// App State
export interface AppState {
  footerMenu: FooterMenuState;
  mainMenu: MainMenuState;
  peopleCards: PeopleCardsState;
  pages: PagesState;
}

const initialFooterMenuState: FooterMenuState = {
  columns: [],
  isLoading: false,
  error: null
};

const initialMainMenuState: MainMenuState = {
  menu_items: [],
  isLoading: false,
  error: null
};

const initialPeopleCardsState: PeopleCardsState = {
  items: [],
  paginatedItems: [],
  footerItems: [],
  totalItem: 0,
  isLoading: false,
  error: null
};

const initialPagesState: PagesState = {
  items: new Map(),
  isLoading: false,
  error: null
};

const initialState: AppState = {
  footerMenu: initialFooterMenuState,
  mainMenu: initialMainMenuState,
  peopleCards: initialPeopleCardsState,
  pages: initialPagesState
};

export const AppStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(state => ({
    // Footer Menu Computed
    footerMenuColumns: computed(() => state.footerMenu().columns),
    footerMenuLoading: computed(() => state.footerMenu().isLoading),
    footerMenuError: computed(() => state.footerMenu().error),
    hasFooterMenuColumns: computed(() => state.footerMenu().columns.length > 0),
    defaultFooterColumns: computed(() =>
      state.footerMenu().columns.filter(col => col.type === FooterMenuColumnType.Default)
    ),
    borderedFooterColumns: computed(() =>
      state.footerMenu().columns.filter(col => col.type === FooterMenuColumnType.Bordered)
    ),

    // Main Menu Computed
    mainMenuItems: computed(() => state.mainMenu().menu_items),
    mainMenuLoading: computed(() => state.mainMenu().isLoading),
    mainMenuError: computed(() => state.mainMenu().error),
    hasMainMenuItems: computed(() => state.mainMenu().menu_items.length > 0),

    // People Cards Computed
    peopleCardsItems: computed(() => state.peopleCards().items),
    peopleCardsPaginatedItems: computed(() => state.peopleCards().paginatedItems),
    peopleCardsFooterItems: computed(() => state.peopleCards().footerItems),
    peopleCardsTotalItem: computed(() => state.peopleCards().totalItem),
    peopleCardsLoading: computed(() => state.peopleCards().isLoading),
    peopleCardsError: computed(() => state.peopleCards().error),
    hasPeopleCardsItems: computed(() => state.peopleCards().items.length > 0),

    // Hero Carousel Computed (extracted from home page)
    heroCarouselItems: computed(() => {
      const homePage = state.pages().items.get(EntityType.HomePage);
      return (
        homePage?.layoutConfig?.hero_carousel?.map(item => ({
          ...item,
          background_asset: item?.background_asset
        })) || []
      );
    }),
    heroCarouselLoading: computed(() => state.pages().isLoading),
    heroCarouselError: computed(() => state.pages().error),
    hasHeroCarouselItems: computed(() => {
      const homePage = state.pages().items.get(EntityType.HomePage);
      return homePage?.layoutConfig?.hero_carousel?.length > 0;
    }),

    // Pages Computed
    pagesItems: computed(() => state.pages().items),
    pagesLoading: computed(() => state.pages().isLoading),
    pagesError: computed(() => state.pages().error),
    hasPagesItems: computed(() => state.pages().items.size > 0)
  })),
  withMethods((store, apiClient = inject(RestApiClientService)) => ({
    // Footer Menu Methods
    fetchFooterMenuColumns: rxMethod<void>(
      switchMap(() => {
        patchState(store, {
          footerMenu: { ...store.footerMenu(), isLoading: true, error: null }
        });

        return apiClient.fetchEntity({ entityType: EntityType.FooterMenu }).pipe(
          map(response => {
            patchState(store, {
              footerMenu: {
                columns: response.columns || [],
                isLoading: false,
                error: null
              }
            });
            return response;
          }),
          catchError(error => {
            patchState(store, {
              footerMenu: {
                ...store.footerMenu(),
                isLoading: false,
                error: error?.message || 'Failed to fetch footer menu columns'
              }
            });
            throw error;
          })
        );
      })
    ),

    // Main Menu Methods
    fetchMainMenuItems: rxMethod<void>(
      switchMap(() => {
        patchState(store, {
          mainMenu: { ...store.mainMenu(), isLoading: true, error: null }
        });

        return apiClient
          .fetchEntity({ entityType: EntityType.MainMenu, populate: 'populate[menu_items][populate]=*' })
          .pipe(
            map(response => {
              patchState(store, {
                mainMenu: {
                  menu_items: response.menu_items || [],
                  isLoading: false,
                  error: null
                }
              });
              return response;
            }),
            catchError(error => {
              patchState(store, {
                mainMenu: {
                  ...store.mainMenu(),
                  isLoading: false,
                  error: error?.message || 'Failed to fetch main menu items'
                }
              });
              throw error;
            })
          );
      })
    ),

    // People Cards Methods
    fetchPeopleCards: rxMethod<{ queryParams?: any } | void>(
      switchMap(res => {
        patchState(store, {
          peopleCards: { ...store.peopleCards(), isLoading: true, error: null }
        });

        return apiClient
          .fetchEntityCollection({
            entityType: EntityType.PeopleCards,
            queryParams: (res as { queryParams?: any })?.queryParams || {},
            populate: peopleCardAttributes()
          })
          .pipe(
            map(response => {
              patchState(store, {
                peopleCards: {
                  ...store.peopleCards(),
                  items: response.data || [],
                  totalItem: response.meta?.pagination?.total || 0,
                  isLoading: false,
                  error: null
                }
              });
              return response;
            }),
            catchError(error => {
              patchState(store, {
                peopleCards: {
                  ...store.peopleCards(),
                  isLoading: false,
                  error: error?.message || 'Failed to fetch people cards'
                }
              });
              throw error;
            })
          );
      })
    ),

    fetchFooterPeopleCards: rxMethod<{ queryParams?: any }>(
      switchMap(({ queryParams }) => {
        return apiClient
          .fetchEntityCollection({
            entityType: EntityType.PeopleCards,
            queryParams
          })
          .pipe(
            map(response => {
              patchState(store, {
                peopleCards: {
                  ...store.peopleCards(),
                  footerItems: response.data || []
                }
              });
              return response;
            }),
            catchError(error => {
              patchState(store, {
                peopleCards: {
                  ...store.peopleCards(),
                  error: error?.message || 'Failed to fetch footer people cards'
                }
              });
              throw error;
            })
          );
      })
    ),

    // Pages Methods
    fetchPage: rxMethod<{ pageName: EntityType }>(
      switchMap(({ pageName }) => {
        patchState(store, {
          pages: { ...store.pages(), isLoading: true, error: null }
        });

        let populate: string;
        switch (pageName) {
          case EntityType.HomePage:
            populate = homePageAttributes();
            break;
          case EntityType.NewsPage:
            populate = newsPageAttributes();
            break;
          default:
            break;
        }

        return apiClient
          .fetchEntity({
            entityType: pageName,
            populate
          })
          .pipe(
            map(response => {
              const currentItems = new Map(store.pages().items);
              currentItems.set(pageName, response);

              patchState(store, {
                pages: {
                  items: currentItems,
                  isLoading: false,
                  error: null
                }
              });
              return response;
            }),
            catchError(error => {
              patchState(store, {
                pages: {
                  ...store.pages(),
                  isLoading: false,
                  error: error?.message || 'Failed to fetch page'
                }
              });
              throw error;
            })
          );
      })
    ),

    // Utility Methods
    resetFooterMenu: () => {
      patchState(store, { footerMenu: initialFooterMenuState });
    },

    resetMainMenu: () => {
      patchState(store, { mainMenu: initialMainMenuState });
    },

    resetPeopleCards: () => {
      patchState(store, { peopleCards: initialPeopleCardsState });
    },

    resetPages: () => {
      patchState(store, { pages: initialPagesState });
    },

    resetAll: () => {
      patchState(store, initialState);
    }
  }))
);
